/**
 * The arithmetic of a row that shows whole cards only. Everything here is a pure function of
 * measured numbers (widths in pixels, taken from the browser's layout), never of how many cards
 * there are.
 *
 * Coordinates: a card sits `step` pixels after the one before it, where `step` is the card width
 * plus the gap. The scroll position `n * step` puts card `n` at the left edge of the view. The view
 * is `viewWidth` wide, edge padding excluded.
 */

/** Layout rounds to a fraction of a pixel; anything closer than this counts as the same place. */
const TOLERANCE_PX = 1;

export type RowLayoutInput = {
  /** The width the cards may fill, edge padding excluded. On desktop it holds the new-one card too. */
  available: number;
  /** The narrowest a card may be: the theme's card width. */
  minCardWidth: number;
  /** The gap between two cards, always the same. */
  gap: number;
  /** How many identity cards the row holds. */
  count: number;
  /** The new-one card sits in the row (desktop) and takes a slot of its own. */
  pinned: boolean;
};

export type RowLayout = {
  /** The cards do not all fit: only whole cards show, and there are arrows. */
  overflowing: boolean;
  /** How many card slots fit the room. */
  slots: number;
  /** The width of every card: the room shared out over the slots, never below the minimum. */
  cardWidth: number;
  /** How many identity cards are in view at rest, and so one page. */
  pageSize: number;
};

/**
 * One card width, from the room alone: as many slots of the minimum card width as fit with the
 * gaps between them, widened to fill the room. The number of cards never changes the width. The
 * new-one card, when it sits in the row, takes a slot too; the row overflows when the identities
 * and that card are more than the slots, and then the new-one card is held at the end and a page
 * is the slots left beside it.
 */
export function computeRowLayout({
  available,
  minCardWidth,
  gap,
  count,
  pinned,
}: Readonly<RowLayoutInput>): RowLayout {
  const slots = Math.max(
    1,
    Math.floor((available + gap + TOLERANCE_PX / 2) / (minCardWidth + gap)),
  );
  const cardWidth = (available - (slots - 1) * gap) / slots;
  const overflowing = count + (pinned ? 1 : 0) > slots;
  const pageSize = Math.max(1, pinned && overflowing ? slots - 1 : slots);
  return { overflowing, slots, cardWidth, pageSize };
}

export type RowView = {
  count: number;
  visibleCount: number;
  cardWidth: number;
  /** The card width plus the gap. */
  step: number;
  viewWidth: number;
};

type Scrolled = { scrollLeft: number };
type ForCard = { index: number };

/** The card nearest to the left edge of the view at a scroll position. */
export function firstIndexAt(scrollLeft: number, step: number): number {
  return Math.max(0, Math.round(scrollLeft / step));
}

/** Whether a card is inside the view from edge to edge. */
export function isCardFullyInView({
  index,
  scrollLeft,
  step,
  cardWidth,
  viewWidth,
}: Readonly<RowView & Scrolled & ForCard>): boolean {
  const start = index * step;
  return (
    start >= scrollLeft - TOLERANCE_PX && start + cardWidth <= scrollLeft + viewWidth + TOLERANCE_PX
  );
}

/**
 * Whether there is more to the left, and more to the right, of what is in view. There is more on a
 * side when the card at that end of the row is not fully in view.
 */
export function moreBeyondView(view: Readonly<RowView & Scrolled>): {
  left: boolean;
  right: boolean;
} {
  if (view.count === 0) {
    return { left: false, right: false };
  }
  return {
    left: !isCardFullyInView({ ...view, index: 0 }),
    right: !isCardFullyInView({ ...view, index: view.count - 1 }),
  };
}

type Paging = Pick<RowView, 'count' | 'visibleCount'>;

function lastWindowStart({ count, visibleCount }: Readonly<Paging>): number {
  return Math.max(0, count - visibleCount);
}

/**
 * The first card of the window that shows `index` with the least movement from the window that
 * starts at `firstIndex`: the same window when the card is already in it, else one that puts the
 * card at the nearer edge. Used to follow keyboard focus.
 */
export function startToReveal({
  index,
  firstIndex,
  count,
  visibleCount,
}: Readonly<Paging & ForCard & { firstIndex: number }>): number {
  const lastIndex = firstIndex + visibleCount - 1;
  if (index < firstIndex) {
    return Math.min(index, lastWindowStart({ count, visibleCount }));
  }
  if (index > lastIndex) {
    return Math.max(0, index - visibleCount + 1);
  }
  return firstIndex;
}

/**
 * Where the window must start so that the chosen card is fully in view, or null when it already
 * is: the least movement that shows it whole.
 */
export function startToShowChosen(view: Readonly<RowView & Scrolled & ForCard>): number | null {
  if (isCardFullyInView(view)) {
    return null;
  }
  return startToReveal({ ...view, firstIndex: firstIndexAt(view.scrollLeft, view.step) });
}

/** The first card of the window one page (the cards in view) on from `firstIndex`, or back. */
export function startAfterPage({
  firstIndex,
  direction,
  count,
  visibleCount,
}: Readonly<Paging & { firstIndex: number; direction: 'left' | 'right' }>): number {
  if (direction === 'left') {
    return Math.max(0, firstIndex - visibleCount);
  }
  return Math.min(firstIndex + visibleCount, lastWindowStart({ count, visibleCount }));
}
